import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MarkdownViewerComponent } from '../../markdown/markdown-viewer/markdown-viewer.component';
import { AuthService } from '../auth.service';
import { MfaSetupComponent } from '../mfa-setup/mfa-setup.component';

@Component({
  selector: 'app-login-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MarkdownViewerComponent,
    MfaSetupComponent
  ],
  templateUrl: './login-dialog.component.html',
  styleUrl: './login-dialog.component.scss'
})
export class LoginDialogComponent {
  private readonly authService = inject(AuthService);
  private readonly dialogRef = inject(MatDialogRef<LoginDialogComponent>);

  hidePassword = true;
  submitting = false;
  errorMessage: string | null = null;
  /** The 403 text is the admin-maintained service message (Markdown); all other errors are plain text. */
  errorIsMarkdown = false;

  view: 'login' | 'forgotPassword' | 'forgotPasswordSent' | 'mfaCode' | 'mfaSetup' = 'login';
  /** Short-lived token (from the password step) the second factor calls run on. */
  mfaToken: string | null = null;
  mfaSubmitting = false;
  mfaError: string | null = null;
  forgotPasswordSubmitting = false;
  forgotPasswordError: string | null = null;

  readonly form = new FormGroup({
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  });

  readonly mfaForm = new FormGroup({
    code: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  });

  readonly forgotPasswordForm = new FormGroup({
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] })
  });

  submit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }

    this.errorMessage = null;
    this.errorIsMarkdown = false;
    this.submitting = true;

    const { email, password } = this.form.getRawValue();
    this.authService.login({ email, password }).subscribe({
      next: response => {
        this.submitting = false;
        if (response.mfaRequired || response.mfaSetupRequired) {
          this.mfaToken = response.mfaToken;
          this.mfaError = null;
          this.mfaForm.reset();
          this.view = response.mfaRequired ? 'mfaCode' : 'mfaSetup';
          return;
        }
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this.errorIsMarkdown = error.status === 403;
        this.errorMessage = this.resolveErrorMessage(error);
      }
    });
  }

  submitMfaCode(): void {
    if (this.mfaForm.invalid || this.mfaSubmitting || !this.mfaToken) {
      this.mfaForm.markAllAsTouched();
      return;
    }

    this.mfaError = null;
    this.mfaSubmitting = true;
    this.authService.verifyMfa(this.mfaToken, this.mfaForm.getRawValue().code).subscribe({
      next: () => {
        this.mfaSubmitting = false;
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.mfaSubmitting = false;
        if (error.status === 401) {
          this.mfaLoginExpired();
          return;
        }
        this.mfaError = error.status === 400 || error.status === 429
          ? (error.error?.message || 'Der Code ist ungültig.')
          : 'Etwas ist schiefgelaufen. Bitte versuche es später erneut.';
      }
    });
  }

  /** Mandatory setup finished (the setup component already stored the token), or backed out of. */
  onMfaSetupCompleted(): void {
    this.dialogRef.close(true);
  }

  onMfaSetupCancelled(event: { expired: boolean }): void {
    if (event.expired) {
      this.mfaLoginExpired();
    } else {
      this.backToLogin();
    }
  }

  private mfaLoginExpired(): void {
    this.view = 'login';
    this.mfaToken = null;
    this.errorIsMarkdown = false;
    this.errorMessage = 'Die Anmeldung ist abgelaufen. Bitte melde dich erneut an.';
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  showForgotPassword(): void {
    this.view = 'forgotPassword';
    this.forgotPasswordError = null;
    this.forgotPasswordForm.reset();
  }

  backToLogin(): void {
    this.view = 'login';
    this.mfaToken = null;
  }

  submitForgotPassword(): void {
    if (this.forgotPasswordForm.invalid || this.forgotPasswordSubmitting) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    this.forgotPasswordError = null;
    this.forgotPasswordSubmitting = true;

    const { email } = this.forgotPasswordForm.getRawValue();
    this.authService.forgotPassword(email).subscribe({
      next: () => {
        this.forgotPasswordSubmitting = false;
        this.view = 'forgotPasswordSent';
      },
      error: () => {
        this.forgotPasswordSubmitting = false;
        this.forgotPasswordError = 'Etwas ist schiefgelaufen. Bitte versuche es später erneut.';
      }
    });
  }

  private resolveErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 401) {
      return 'Invalid email or password.';
    }
    if (error.status === 403) {
      return error.error?.message || 'Der Kundenbereich ist derzeit nicht verfügbar.';
    }
    return 'Something went wrong. Please try again later.';
  }
}
